import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Tooltip, Tree, message } from "antd";
import {
  ArrowsClockwise,
  Export,
  Folder,
  FolderSimplePlus,
  LinkSimple,
  MicrosoftWordLogo,
  PencilSimple,
  ShareNetwork,
  Trash,
  UploadSimple,
  X,
} from "@phosphor-icons/react";
import {
  createPersonalFolder,
  deletePersonalDocument,
  deletePersonalFolder,
  fetchPersonalHierarchy,
  movePersonalDocument,
  openPersonalDocumentInWord,
  renamePersonalDocument,
  renamePersonalFolder,
  syncPersonalDocumentFromWord,
  uploadPersonalDocument,
} from "../../service/personalStorage";
import { getFileIconBySize, formatFileSize, isEpubFile } from "../../utils/fileUtils";
import ShareFileModal from "../../components/ExternalShare/ShareFileModal";
import ShareFolderModal from "../../components/ExternalShare/ShareFolderModal";
import ReadOnlyDocumentViewer from "../../components/Document/ReadOnlyDocumentViewer";
import { useUploadNotification } from "../../context/UploadNotificationContext";
import CreateFolderModal from "./CreateFolderModal";
import RenameItemModal from "./RenameItemModal";
import MoveDocumentModal from "./MoveDocumentModal";
import CopyLinkModal from "./CopyLinkModal";
import EpubReader from "./EpubReader";
import { DocumentActionsMenu, FolderActionsMenu } from "./PersonalItemActions";
import {
  breadcrumbTrail,
  buildFolderTree,
  childrenOf,
  documentNodeKey,
  folderNodeKey,
} from "./personalTreeUtils";
import {
  Body,
  Breadcrumb,
  BreadcrumbCrumb,
  EmptyState,
  ErrorBanner,
  Header,
  HeaderActions,
  ListBody,
  ListPanel,
  Page,
  Row,
  RowIcon,
  RowMeta,
  RowName,
  Subtitle,
  Title,
  TreePanel,
  ViewerActionButton,
  ViewerActions,
  ViewerBody,
  ViewerCloseButton,
  ViewerFileName,
  ViewerPanel,
  ViewerTitleGroup,
  ViewerToolbar,
  ViewerTrail,
} from "./MyFilesStyled";

const handleSnackbar = (text, success = false) => {
  if (success) message.success(text);
  else message.error(text || "Something went wrong.");
};

/**
 * "My Files" — one personal folder tree per user, separate from any Matter.
 * See docs/build-checklist plan "Personal Storage Areas": Share for review
 * is deliberately unavailable here; External Share works exactly as it does
 * for case documents (see documents/views/document_share.py for the block,
 * documents/services/personal_storage.py for the storage/copy-link model).
 *
 * Layout mirrors the Matter file management pages: a folder tree on the
 * left, and a right-hand panel that shows either the current folder's
 * contents or — once a document is selected — that document inline with a
 * small actions toolbar at the top (no modal, no new tab).
 *
 * Word and Excel files open in Word through a copy in the owner's own
 * OneDrive (documents/services/personal_word_copy.py); WHG Docs pulls the
 * saved copy back when the document is opened here, when the person returns
 * to this tab, and on "Refresh Document Changes".
 */
const MyFiles = () => {
  const [hierarchy, setHierarchy] = useState({ root_id: null, folders: [], documents: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [uploading, setUploading] = useState(false);
  const { addUpload, updateUpload, markUploadCompleted, removeUpload: removeUploadNotification } =
    useUploadNotification();

  const [createFolderTarget, setCreateFolderTarget] = useState(null);
  const [renameItem, setRenameItem] = useState(null);
  const [moveDocument, setMoveDocument] = useState(null);
  const [copyLinkDocument, setCopyLinkDocument] = useState(null);
  const [externalShareDocument, setExternalShareDocument] = useState(null);
  const [externalShareFolder, setExternalShareFolder] = useState(null);

  const fileInputRef = useRef(null);

  const [viewerVersion, setViewerVersion] = useState(0);
  const [openingWord, setOpeningWord] = useState(false);
  const [refreshingWordId, setRefreshingWordId] = useState(null);
  const wordSyncs = useRef(new Map());
  const selectedIdRef = useRef(null);

  useEffect(() => {
    selectedIdRef.current = selectedDocument?.id ?? null;
  }, [selectedDocument?.id]);

  const applyDocumentUpdate = useCallback((updated) => {
    if (!updated?.id) return;
    const sameDocument = (document) => String(document?.id) === String(updated.id);
    setHierarchy((current) => ({
      ...current,
      documents: current.documents.map((document) =>
        sameDocument(document) ? { ...document, ...updated } : document
      ),
    }));
    setSelectedDocument((current) =>
      sameDocument(current) ? { ...current, ...updated } : current
    );
  }, []);

  const syncFromWord = useCallback(
    async (document, { quiet = false } = {}) => {
      if (!document?.id) return;
      const key = String(document.id);
      let request = wordSyncs.current.get(key);
      if (!request) {
        request = syncPersonalDocumentFromWord(document.id)
          .then(({ data }) => {
            applyDocumentUpdate(data?.document);
            if (String(selectedIdRef.current) === key) {
              if (data?.changed) {
                setViewerVersion((version) => version + 1);
                message.success(data.detail);
              } else if (data?.missing) {
                message.warning(data.detail);
              }
            }
            return data;
          })
          .finally(() => wordSyncs.current.delete(key));
        wordSyncs.current.set(key, request);
      }
      if (quiet) {
        request.catch(() => {});
        return;
      }
      setRefreshingWordId(key);
      try {
        const data = await request;
        if (!data?.changed && !data?.missing && String(selectedIdRef.current) === key) {
          message.info(data?.detail || "Already up to date with Word.");
        }
      } catch (requestError) {
        if (String(selectedIdRef.current) !== key) return;
        message.error(
          requestError?.response?.data?.error ||
            "Could not bring in your changes from Word."
        );
      } finally {
        setRefreshingWordId((current) => (current === key ? null : current));
      }
    },
    [applyDocumentUpdate]
  );

  const handleEditInWord = async (document) => {
    // Opened before the request so pop-up blockers treat it as the click's own tab.
    const tab = window.open("", "_blank");
    setOpeningWord(true);
    try {
      const { data } = await openPersonalDocumentInWord(document.id);
      if (tab) {
        tab.opener = null;
        tab.location = data.web_url;
      } else {
        window.open(data.web_url, "_blank", "noopener,noreferrer");
      }
      applyDocumentUpdate({ id: document.id, has_word_copy: true });
    } catch (requestError) {
      if (tab) tab.close();
      message.error(
        requestError?.response?.data?.error || "Could not open this file in Word."
      );
    } finally {
      setOpeningWord(false);
    }
  };

  const selectedWordDocumentId = selectedDocument?.has_word_copy ? selectedDocument.id : null;

  useEffect(() => {
    if (!selectedWordDocumentId) return undefined;
    const onFocus = () => syncFromWord({ id: selectedWordDocumentId }, { quiet: true });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [selectedWordDocumentId, syncFromWord]);

  const loadHierarchy = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetchPersonalHierarchy();
      const data = response?.data || { root_id: null, folders: [], documents: [] };
      setHierarchy(data);
      setSelectedFolderId((current) => current || data.root_id);
      setExpandedKeys((current) =>
        current.length ? current : [data.root_id ? `F${data.root_id}` : "root"]
      );
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ||
          "Could not load My Files. Refresh to try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHierarchy();
  }, [loadHierarchy]);

  const treeData = useMemo(
    () =>
      hierarchy.root_id
        ? buildFolderTree(hierarchy.root_id, hierarchy.folders, hierarchy.documents)
        : [],
    [hierarchy]
  );

  const trail = useMemo(
    () =>
      selectedFolderId
        ? breadcrumbTrail(selectedFolderId, hierarchy.folders, hierarchy.root_id)
        : [],
    [selectedFolderId, hierarchy]
  );

  const { folders: childFolders, documents: childDocuments } = useMemo(
    () =>
      selectedFolderId
        ? childrenOf(selectedFolderId, hierarchy.folders, hierarchy.documents)
        : { folders: [], documents: [] },
    [selectedFolderId, hierarchy]
  );

  const selectFolder = (folderId) => {
    selectedIdRef.current = null;
    setSelectedDocument(null);
    setSelectedFolderId(folderId);
  };

  const openDocument = (document) => {
    selectedIdRef.current = document?.id ?? null;
    setSelectedDocument(document);
    if (document?.has_word_copy) syncFromWord(document, { quiet: true });
  };
  const closeDocument = () => {
    selectedIdRef.current = null;
    setSelectedDocument(null);
  };

  const handleCreateFolder = async (name) => {
    const parentId = createFolderTarget?.id || selectedFolderId;
    const response = await createPersonalFolder({ name, parent_id: parentId });
    setCreateFolderTarget(null);
    message.success(`Folder "${response?.data?.name || name}" created.`);
    await loadHierarchy();
  };

  const handleRename = async (newName) => {
    if (renameItem.type === "folder") {
      await renamePersonalFolder(renameItem.id, newName);
    } else {
      const response = await renamePersonalDocument(renameItem.id, newName);
      if (selectedDocument?.id === renameItem.id) {
        setSelectedDocument(response?.data || { ...selectedDocument, name: newName });
      }
    }
    setRenameItem(null);
    message.success("Renamed.");
    await loadHierarchy();
  };

  const handleDeleteFolder = (folder) => {
    Modal_confirmDelete({
      title: `Delete "${folder.name}"?`,
      content: "This deletes the folder and everything inside it.",
      onOk: async () => {
        await deletePersonalFolder(folder.id);
        if (selectedFolderId === folder.id) selectFolder(hierarchy.root_id);
        else closeDocument();
        message.success("Folder deleted.");
        await loadHierarchy();
      },
    });
  };

  const handleDeleteDocument = (document) => {
    Modal_confirmDelete({
      title: `Delete "${document.name}"?`,
      content: "This cannot be undone.",
      onOk: async () => {
        await deletePersonalDocument(document.id);
        if (selectedDocument?.id === document.id) closeDocument();
        message.success("File deleted.");
        await loadHierarchy();
      },
    });
  };

  const handleMove = async (destinationFolderId) => {
    const response = await movePersonalDocument(moveDocument.id, destinationFolderId);
    if (selectedDocument?.id === moveDocument.id) {
      setSelectedDocument(response?.data || selectedDocument);
    }
    setMoveDocument(null);
    message.success("File moved.");
    await loadHierarchy();
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || !selectedFolderId) return;
    setUploading(true);
    let failureCount = 0;
    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const uploadId = `my-files-upload-${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`;
        // The same floating tray Matter document uploads use (see
        // GlobalUploadNotification, mounted once in App.js) — `skipOptimization`
        // opts out of its "optimize for fast web view" stage, which My Files
        // never runs (see EnhancedUploadNotification.getUploadStatus).
        addUpload({
          id: uploadId,
          fileName: file.name,
          fileSize: file.size,
          type: file.name.split(".").pop()?.toLowerCase(),
          status: "uploading",
          progress: 0,
          uploadIndex: index + 1,
          totalUploads: files.length,
          skipOptimization: true,
          matterLabel: "My Files (personal)",
        });

        const formData = new FormData();
        formData.append("file", file);
        formData.append("folder_id", selectedFolderId);
        try {
          // eslint-disable-next-line no-await-in-loop
          await uploadPersonalDocument(formData, {
            onUploadProgress: (progressEvent) => {
              const total = progressEvent.total || file.size || 0;
              const percent = total
                ? Math.min(100, Math.round((progressEvent.loaded * 100) / total))
                : 0;
              updateUpload(uploadId, { progress: percent });
            },
          });
          markUploadCompleted(uploadId);
          updateUpload(uploadId, { progress: 100, status: "completed" });
        } catch (requestError) {
          failureCount += 1;
          removeUploadNotification(uploadId);
          message.error(
            requestError?.code === "ECONNABORTED"
              ? `"${file.name}" timed out uploading — the file may be large or your connection slow.`
              : requestError?.response?.data?.error || `"${file.name}" failed to upload.`
          );
        }
      }
      if (failureCount < files.length) {
        message.success(
          files.length - failureCount > 1 ? "Files uploaded." : "File uploaded."
        );
      }
      await loadHierarchy();
    } finally {
      setUploading(false);
    }
  };

  const viewerTrailLabel = trail
    .map((folder) => (folder.is_root ? "My Files" : folder.name))
    .join(" / ");

  return (
    <Page>
      <Header>
        <div>
          <Title>My Files</Title>
          <Subtitle>
            Your own private folders — a stash for templates and references
            you use often. Not a place for live matter work: there&apos;s no
            Send for Review here, and usage is monitored. Word and Excel
            files open in Word from a copy in your OneDrive.
          </Subtitle>
        </div>
        <HeaderActions>
          <button
            type="button"
            onClick={() => setCreateFolderTarget(trail.at(-1) || { id: hierarchy.root_id })}
            style={buttonStyle()}
          >
            <FolderSimplePlus size={16} /> New folder
          </button>
          <button
            type="button"
            onClick={handleUploadClick}
            disabled={uploading}
            style={buttonStyle(true)}
          >
            <UploadSimple size={16} /> {uploading ? "Uploading…" : "Upload"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            data-testid="my-files-upload-input"
            onChange={handleFilesSelected}
          />
        </HeaderActions>
      </Header>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <Body>
        <TreePanel>
          {treeData.length > 0 && (
            <Tree
              treeData={treeData}
              selectedKeys={
                selectedDocument
                  ? [documentNodeKey(selectedDocument)]
                  : selectedFolderId
                  ? [folderNodeKey({ id: selectedFolderId })]
                  : []
              }
              expandedKeys={expandedKeys}
              onExpand={setExpandedKeys}
              onSelect={(_keys, info) => {
                if (info.node?.type === "document" && info.node?.record) {
                  openDocument(info.node.record);
                  return;
                }
                if (info.node?.record?.id) selectFolder(info.node.record.id);
              }}
              icon={(node) =>
                node.type === "document" ? (
                  getFileIconBySize(node.record?.name, "document", 14)
                ) : (
                  <Folder size={16} color="#5A6C87" />
                )
              }
              showIcon
            />
          )}
        </TreePanel>

        <ListPanel>
          {selectedDocument ? (
            <ViewerPanel>
              <ViewerToolbar>
                <ViewerTitleGroup>
                  <ViewerTrail>{viewerTrailLabel}</ViewerTrail>
                  <ViewerFileName>{selectedDocument.name}</ViewerFileName>
                </ViewerTitleGroup>
                <ViewerActions>
                  {selectedDocument.word_editable && (
                    <ViewerActionButton
                      type="button"
                      disabled={openingWord}
                      onClick={() => handleEditInWord(selectedDocument)}
                    >
                      <MicrosoftWordLogo size={14} />{" "}
                      {openingWord ? "Opening…" : "Edit Document"}
                    </ViewerActionButton>
                  )}
                  {selectedDocument.word_editable && selectedDocument.has_word_copy && (
                    <Tooltip title="Use this after editing in Word to show the latest saved version in WHG Docs.">
                      <ViewerActionButton
                        type="button"
                        disabled={refreshingWordId === String(selectedDocument.id)}
                        onClick={() => syncFromWord(selectedDocument)}
                      >
                        <ArrowsClockwise size={14} />{" "}
                        {refreshingWordId === String(selectedDocument.id)
                          ? "Refreshing…"
                          : "Refresh Document Changes"}
                      </ViewerActionButton>
                    </Tooltip>
                  )}
                  <ViewerActionButton
                    type="button"
                    onClick={() => setRenameItem({ ...selectedDocument, type: "document" })}
                  >
                    <PencilSimple size={14} /> Rename
                  </ViewerActionButton>
                  <ViewerActionButton type="button" onClick={() => setMoveDocument(selectedDocument)}>
                    <Export size={14} /> Move
                  </ViewerActionButton>
                  <ViewerActionButton type="button" onClick={() => setCopyLinkDocument(selectedDocument)}>
                    <LinkSimple size={14} /> Copy link
                  </ViewerActionButton>
                  <ViewerActionButton
                    type="button"
                    onClick={() => setExternalShareDocument(selectedDocument)}
                  >
                    <ShareNetwork size={14} /> External Share
                  </ViewerActionButton>
                  <ViewerActionButton
                    type="button"
                    $danger
                    onClick={() => handleDeleteDocument(selectedDocument)}
                  >
                    <Trash size={14} /> Delete
                  </ViewerActionButton>
                  <ViewerCloseButton
                    type="button"
                    aria-label="Close preview"
                    onClick={closeDocument}
                  >
                    <X size={16} />
                  </ViewerCloseButton>
                </ViewerActions>
              </ViewerToolbar>
              <ViewerBody>
                {isEpubFile(selectedDocument.name) ? (
                  <EpubReader embedded document={selectedDocument} />
                ) : (
                  <ReadOnlyDocumentViewer
                    key={`${selectedDocument.id}:${viewerVersion}`}
                    documentId={selectedDocument.id}
                    filename={selectedDocument.name}
                  />
                )}
              </ViewerBody>
            </ViewerPanel>
          ) : (
            <>
              <Breadcrumb>
                {trail.map((folder, index) => (
                  <span key={folder.id} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {index > 0 && <span>/</span>}
                    <BreadcrumbCrumb
                      type="button"
                      $current={index === trail.length - 1}
                      onClick={() => selectFolder(folder.id)}
                    >
                      {folder.is_root ? "My Files" : folder.name}
                    </BreadcrumbCrumb>
                  </span>
                ))}
              </Breadcrumb>

              <ListBody>
                {!loading && childFolders.length === 0 && childDocuments.length === 0 && (
                  <EmptyState>
                    This folder is empty. Upload a file or create a subfolder to
                    get started.
                  </EmptyState>
                )}

                {childFolders.map((folder) => (
                  <Row
                    key={folder.id}
                    $clickable
                    onClick={() => selectFolder(folder.id)}
                  >
                    <RowIcon>
                      <Folder size={18} color="#5A6C87" />
                    </RowIcon>
                    <RowName>{folder.name}</RowName>
                    <RowMeta>Folder</RowMeta>
                    <FolderActionsMenu
                      folder={folder}
                      isRoot={false}
                      onNewSubfolder={setCreateFolderTarget}
                      onRename={(item) => setRenameItem({ ...item, type: "folder" })}
                      onDelete={handleDeleteFolder}
                      onExternalShare={setExternalShareFolder}
                    />
                  </Row>
                ))}

                {childDocuments.map((document) => (
                  <Row key={document.id} $clickable onClick={() => openDocument(document)}>
                    <RowIcon>{getFileIconBySize(document.name, "document", 18)}</RowIcon>
                    <RowName>{document.name}</RowName>
                    <RowMeta>{formatFileSize(document.size)}</RowMeta>
                    <DocumentActionsMenu
                      document={document}
                      onOpen={openDocument}
                      onRename={(item) => setRenameItem({ ...item, type: "document" })}
                      onMove={setMoveDocument}
                      onCopyLink={setCopyLinkDocument}
                      onExternalShare={setExternalShareDocument}
                      onDelete={handleDeleteDocument}
                    />
                  </Row>
                ))}
              </ListBody>
            </>
          )}
        </ListPanel>
      </Body>

      <CreateFolderModal
        open={Boolean(createFolderTarget)}
        onClose={() => setCreateFolderTarget(null)}
        onCreate={handleCreateFolder}
      />

      <RenameItemModal
        open={Boolean(renameItem)}
        item={renameItem}
        onClose={() => setRenameItem(null)}
        onRename={handleRename}
      />

      <MoveDocumentModal
        open={Boolean(moveDocument)}
        document={moveDocument}
        rootId={hierarchy.root_id}
        folders={hierarchy.folders}
        onClose={() => setMoveDocument(null)}
        onMove={handleMove}
      />

      <CopyLinkModal
        open={Boolean(copyLinkDocument)}
        document={copyLinkDocument}
        onClose={() => setCopyLinkDocument(null)}
      />

      {externalShareDocument && (
        <ShareFileModal
          isOpen={Boolean(externalShareDocument)}
          onClose={() => setExternalShareDocument(null)}
          selectedDoc={externalShareDocument}
          document_id={String(externalShareDocument.id)}
          handleSnackbar={handleSnackbar}
        />
      )}

      {externalShareFolder && (
        <ShareFolderModal
          isOpen={Boolean(externalShareFolder)}
          onClose={() => setExternalShareFolder(null)}
          folder_id={String(externalShareFolder.id)}
          handleSnackbar={handleSnackbar}
        />
      )}
    </Page>
  );
};

function Modal_confirmDelete({ title, content, onOk }) {
  Modal.confirm({
    title,
    content,
    okText: "Delete",
    okButtonProps: { danger: true },
    onOk,
  });
}

const buttonStyle = (primary = false) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  borderRadius: 8,
  border: primary ? "1px solid #0c53bb" : "1px solid #dde2e9",
  background: primary ? "#0c53bb" : "#fff",
  color: primary ? "#fff" : "#344054",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
});

export default MyFiles;
